import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MaterialModule } from 'src/app/core/material.module';
import { NotificationService } from 'src/app/core/services/notification.service';
import { StorageBrowser } from 'src/app/core/services/storage.service';
import { UserService } from 'src/app/core/services/user.service';
import { AppEditorComponent } from '../../../app-editor/app-editor.component';
import { FileUploadComponent } from "../../../app-file-upload/app-file-upload.component";
import { Asset } from '../../../app-file-upload/asset.interface';
import { ProfileService } from '../../profile.service';
import { SsoSecurityService } from '../../sso-security.service';
import {
  DateFormat,
  HeightUnit,
  TimeFormat,
  WeightUnit,
} from '../interface/interface';

function defaultNavigate(url: string): void {
  window.location.assign(url);
}

@Component({
  selector: 'app-profile-dialog',
  templateUrl: './profile-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    AppEditorComponent,
    FileUploadComponent,
    MatSlideToggleModule
],
})
export class ProfileDialogComponent {


 signatureImage: Asset | null = null;
  profileForm: FormGroup;
  currUser: any;
  isLoading: boolean = false;

  /**
   * Whether this Lab can send the user off to manage their sign-in security.
   *
   * False until the server says otherwise, so nothing flickers into view on a
   * Lab running without SSO, or for an account that has no SSO identity.
   */
  ssoSecurityAvailable = false;
  isOpeningSsoSecurity = false;

  // Dynamic options from Enums
  weightOptions = Object.values(WeightUnit);
  heightOptions = Object.values(HeightUnit);
  timeFormatOptions = Object.values(TimeFormat);
  dateFormatOptions = Object.values(DateFormat);

  // Enum references for template
  weightUnits = WeightUnit;
  heightUnits = HeightUnit;
  timeFormats = TimeFormat;
  dateFormats = DateFormat;

  constructor(
    public storageBrowser: StorageBrowser,
    public profileService: ProfileService,
    public userService: UserService,
    public notificationService: NotificationService,

    public ssoSecurityService: SsoSecurityService,

    private fb: FormBuilder,
    public dialogRef: MatDialogRef<ProfileDialogComponent>
  ) {
    this.profileForm = this.fb.group({
      firstName: [''],
      lastName: [''],
      email: [{ value: '', disabled: true }],
      preferredWeightUnit: [WeightUnit.KG],
      preferredLengthUnit: [HeightUnit.CM],
      preferredTimeFormat: [TimeFormat.H24],
      preferredDateFormat: [DateFormat.DMY],
      profilePicture: [''],
      profileSettings: this.fb.group({
        signatureEnabledPdf: [false],
        signature: [''],
        signatureImageAssetId: [null],
      }),
      notificationSettings: this.fb.group({
        isEnabled: [true],
        labUserStatusEmailTriggers: [true],
        labUserAlgoFailureEmailTriggers: [true],
      }),
    });
  }

  ngOnInit() {
    this.loadSsoSecurity();
    this.currUser = this.userService.currentUser.getValue();
    if (this.currUser) {
      this.profileForm.patchValue({
        firstName: this.currUser.firstName || '',
        lastName: this.currUser.lastName || '',
        email: this.currUser.email || '',
        preferredWeightUnit: this.currUser.preferredWeightUnit || WeightUnit.LB,
        preferredLengthUnit: this.currUser.preferredLengthUnit || HeightUnit.IN,
        preferredTimeFormat:
          this.currUser.preferredTimeFormat || TimeFormat.H24,
        preferredDateFormat:
          this.currUser.preferredDateFormat || DateFormat.MDY,
        profilePicture: this.currUser.profilePicture || '',
        profileSettings: {
          signature: this.currUser.profileSettings?.signature || '',
          signatureEnabledPdf: this.currUser.profileSettings?.signatureEnabledPdf || false,
          signatureImageAssetId: this.currUser.profileSettings?.signatureImage?.id || null,
        },
         notificationSettings: {
            isEnabled: this.currUser.notificationSettings?.isEnabled  != false,
            labUserStatusEmailTriggers: this.currUser.notificationSettings?.labUserStatusEmailTriggers  != false,
            labUserAlgoFailureEmailTriggers:  this.currUser.notificationSettings?.labUserAlgoFailureEmailTriggers  != false,
         }
      });

      this.signatureImage = this.currUser.profileSettings?.signatureImage
    }
    this.profileForm.get('profileSettings.signatureEnabledPdf')?.valueChanges.subscribe(
    (enabled: boolean) => {
      const signatureControl = this.profileForm.get('profileSettings.signature');

      if (enabled && !signatureControl?.value?.trim()?.length) {
        signatureControl?.setValue(`${this.currUser.fullName}
${this.currUser.title ? this.currUser.title +','  : ''} ${this.currUser?.department?.name ? this.currUser.department.name : ''}
License No: _________
        `);
      }
    }
  );

  }

   onSignatureImageUploadSuccess
(asset: Asset | null){
    if(asset) {
      this.profileForm.patchValue({profileSettings: {signatureImageAssetId: asset.id}});
    }else if(asset == null){
     this.profileForm.patchValue({profileSettings: {signatureImageAssetId: null}});
      this.signatureImage = null
    }
  }

  onSignatureChange(signature: string) {
    this.profileForm.patchValue({
      profileSettings: {
        signature
      },
    });
  }


  /**
   * Ask whether to offer the security button. Silent on failure — a profile
   * dialog is no place to raise an error about a button nobody asked for.
   */
  private loadSsoSecurity() {
    this.ssoSecurityService.isAvailable().subscribe({
      next: (res) => (this.ssoSecurityAvailable = !!res?.available),
      error: () => (this.ssoSecurityAvailable = false),
    });
  }

  /**
   * Hand the browser over to SSO's account-security pages.
   *
   * The link is fetched at this moment rather than when the dialog opened,
   * because the handoff it carries lives for only a couple of minutes — long
   * enough for a redirect, not long enough to sit in a form somebody is
   * reading.
   *
   * `navigate` is injectable because jsdom refuses to let a test redefine
   * `window.location`.
   */
  openSsoSecurity(navigate: (url: string) => void = defaultNavigate) {
    if (this.isOpeningSsoSecurity) {
      return;
    }
    this.isOpeningSsoSecurity = true;
    this.ssoSecurityService.mfaSetupLink().subscribe({
      next: (res) => {
        if (!res?.url) {
          this.isOpeningSsoSecurity = false;
          this.notificationService.error(
            'Could not open your security settings. Please try again.'
          );
          return;
        }
        // Leaving this origin entirely, so the dialog goes with it.
        this.dialogRef.close();
        navigate(res.url);
      },
      error: () => {
        this.isOpeningSsoSecurity = false;
        this.notificationService.error(
          'Could not open your security settings. Please try again.'
        );
      },
    });
  }

  saveChanges() {
    this.currUser = this.userService.currentUser.getValue();
    const value = this.profileForm.value;
    this.isLoading = true;
    this.profileService.updateProfile(value).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.currUser.preferredWeightUnit = res.preferredWeightUnit;
        this.currUser.preferredLengthUnit = res.preferredLengthUnit;
        this.currUser.preferredTimeFormat = res.preferredTimeFormat;
        this.currUser.preferredDateFormat = res.preferredDateFormat;
        this.currUser.firstName = res.firstName;
        this.currUser.lastName = res.lastName;
        this.currUser.profileSettings = res.profileSettings;
        this.currUser.notificationSettings = res.notificationSettings;
        this.userService.updateUserData(this.currUser);

        this.notificationService.success('Saved Successfully');
        this.dialogRef.close(this.profileForm.value);
      },
      error: () => {
        this.isLoading = false;
        this.notificationService.error('Error in saving Profile');
      },
    });
  }
}
